import { useEffect } from 'react';
import { ROLES } from '../core/constants/roles';
import {
  DEMO_PLAY_FEED_INTERVAL_MS,
  DEMO_PLAY_FEED_MAX,
  DEMO_TICK_INTERVAL_MS,
} from '../services/demo/demoTicksConfig';

interface DemoTickAdapter {
  name?: string;
  runDemoTick?: () => Promise<unknown>;
  runDemoPlayTick?: () => Promise<unknown>;
}

interface UseDemoTicksOptions {
  /** Authenticated role — non-listers get a capped live play feed. */
  role?: string;
}

/** Periodically simulates multi-role activity in mock mode. */
export function useDemoTicks(
  adapter: DemoTickAdapter | null | undefined,
  enabled: boolean,
  options: UseDemoTicksOptions = {},
) {
  const { role } = options;
  const playFeed = Boolean(role && role !== ROLES.LISTER);

  useEffect(() => {
    if (!enabled || adapter?.name !== 'mock' || !adapter.runDemoTick) return undefined;

    const tick = () => {
      adapter.runDemoTick?.().catch((err) => {
        console.warn('[useDemoTicks]', err);
      });
    };

    const id = window.setInterval(tick, DEMO_TICK_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [adapter, enabled]);

  useEffect(() => {
    if (!enabled || !playFeed || adapter?.name !== 'mock' || !adapter.runDemoPlayTick) {
      return undefined;
    }

    let created = 0;
    const tick = () => {
      if (created >= DEMO_PLAY_FEED_MAX) return;
      created += 1;
      adapter.runDemoPlayTick?.().catch((err) => {
        console.warn('[useDemoTicks:playFeed]', err);
      });
    };

    // First play soon so the banker sees live registry activity without waiting a full interval.
    const firstId = window.setTimeout(tick, 1_200);
    const id = window.setInterval(tick, DEMO_PLAY_FEED_INTERVAL_MS);
    return () => {
      window.clearTimeout(firstId);
      window.clearInterval(id);
    };
  }, [adapter, enabled, playFeed]);
}
